import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import UsageWidget from "@/components/UsageWidget";
import { toast } from "sonner";
import {
  ArrowRight, ArrowUpRight, CheckCircle, CreditCard, Receipt, Shield,
  Sparkle, WarningCircle, Calendar as CalIcon, GearSix,
} from "@phosphor-icons/react";

const STATUS_STYLES = {
  active: "bg-teal-soft text-teal",
  past_due: "bg-amber-100 text-amber-700",
  cancelled: "bg-secondary text-muted-foreground",
  paused: "bg-secondary text-muted-foreground",
  none: "bg-secondary text-muted-foreground",
};

function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

function fmtAmount(amount, currency) {
  if (amount == null) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency: currency || "XOF",
      maximumFractionDigits: ["XOF", "NGN", "KES"].includes(currency || "XOF") ? 0 : 2,
    }).format(amount);
  } catch { return `${amount} ${currency || ""}`; }
}

export default function Billing() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, h, p] = await Promise.all([
          api.get("/subscription/status"),
          api.get("/subscription/history").catch(() => ({ data: [] })),
          api.get("/subscription/plans").catch(() => ({ data: { plans: [] } })),
        ]);
        setStatus(s.data);
        setHistory(Array.isArray(h.data) ? h.data : []);
        setPlans(p.data?.plans || []);
      } catch (e) { /* noop */ }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="p-8 lg:p-12 text-sm text-muted-foreground" data-testid="billing-loading">Loading billing…</div>;
  if (!status) return <div className="p-8 lg:p-12 text-sm text-muted-foreground">Could not load subscription details.</div>;

  const planKey = status.plan_key || "explorer";
  const isPaid = planKey !== "explorer" && planKey !== "discovery";
  const statusStyle = STATUS_STYLES[status.status] || STATUS_STYLES.active;
  const currentPlanMeta = plans.find((p) => p.key === planKey);
  const upgradeTargets = plans.filter((p) => !p.contact_only && p.key !== planKey && p.key !== "explorer");
  const showUpgrade = planKey === "explorer" || planKey === "starter";
  const paddle = status.paddle || {};
  const hasPaddleSub = !!paddle.subscription_id;
  const scheduled = paddle.scheduled_change || null;
  const billingIntervalLabel = (paddle.billing_interval || "").toLowerCase() === "year" ? "Annual" : "Monthly";

  async function openPaddlePortal() {
    try {
      const r = await api.get("/paddle/portal");
      const url = r?.data?.portal_url;
      if (!url) throw new Error("No portal URL returned.");
      window.location.href = url;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Could not open the Paddle portal.";
      toast.error(String(msg));
    }
  }

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="billing-page">
      <PageHeader
        eyebrow="Subscription & billing"
        title="Your plan and usage"
        subtitle="Track your current subscription, monthly quotas and payment history."
        action={
          hasPaddleSub ? (
            <button
              type="button"
              onClick={openPaddlePortal}
              className="hidden sm:inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:opacity-90 card-elev"
              data-testid="billing-manage-subscription"
            >
              <GearSix size={14} weight="bold" /> Manage subscription
            </button>
          ) : isPaid ? (
            <a
              href="mailto:billing@orixa-invest.co?subject=Manage%20my%20Orixa%20Invest%20subscription"
              className="hidden sm:inline-flex items-center gap-2 border border-border rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-secondary"
              data-testid="billing-contact"
            >
              <Receipt size={14} weight="bold" /> Billing support
            </a>
          ) : null
        }
      />

      {/* Current plan card */}
      <div className="mt-8 bg-card border border-border rounded-2xl p-5 sm:p-8 card-elev" data-testid="billing-current-plan">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
          <div className="flex-1">
            <div className="text-overline text-primary flex items-center gap-2"><Sparkle size={12} weight="fill" /> Current plan</div>
            <div className="mt-2 flex items-baseline flex-wrap gap-3">
              <div className="font-display text-3xl sm:text-4xl tracking-tight">Orixa {status.plan_label}</div>
              <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 ${statusStyle}`}>{status.status}</span>
            </div>
            {status.plan_tagline && (
              <div className="mt-2 text-sm text-muted-foreground max-w-md">{status.plan_tagline}</div>
            )}
            {currentPlanMeta?.price ? (
              <div className="mt-4 text-sm">
                <span className="data-large text-2xl text-primary">
                  {fmtAmount(currentPlanMeta.local_price, currentPlanMeta.currency)}
                </span>
                <span className="text-muted-foreground"> / month</span>
              </div>
            ) : planKey === "explorer" ? (
              <div className="mt-4 text-sm text-muted-foreground">Free plan</div>
            ) : null}
          </div>

          <div className="flex flex-col gap-3 md:w-72">
            <div className="rounded-lg border border-border p-4 bg-secondary/30">
              <div className="flex items-center gap-2 text-overline text-muted-foreground">
                <CalIcon size={12} weight="bold" /> Billing period
              </div>
              <div className="mt-2 text-sm" data-testid="billing-period-info">
                {hasPaddleSub && (
                  <div>Interval <span className="font-semibold capitalize" data-testid="billing-interval">{billingIntervalLabel}</span></div>
                )}
                <div className="mt-1">Started <span className="font-semibold">{fmtDate(paddle.billing_period_start || status.start_date)}</span></div>
                <div className="mt-1">
                  {(paddle.billing_period_end || status.next_payment_date) ? (
                    <>Renews <span className="font-semibold" data-testid="billing-renews">{fmtDate(paddle.billing_period_end || status.next_payment_date)}</span></>
                  ) : (
                    <>Quota resets <span className="font-semibold">{fmtDate(status.period_resets_at)}</span></>
                  )}
                </div>
                {scheduled?.effective_at && (
                  <div className="mt-2 pt-2 border-t border-border text-xs text-amber-700" data-testid="billing-scheduled">
                    <span className="font-semibold uppercase tracking-wider">Scheduled</span>{" "}
                    {scheduled.action || "change"} on {fmtDate(scheduled.effective_at)}
                  </div>
                )}
              </div>
            </div>
            {hasPaddleSub ? (
              <button
                type="button"
                onClick={openPaddlePortal}
                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-3 text-sm font-semibold hover:opacity-90 card-elev"
                data-testid="billing-manage-cta"
              >
                <GearSix size={14} weight="bold" /> Manage subscription
              </button>
            ) : showUpgrade ? (
              <Link
                to={`/pricing`}
                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-3 text-sm font-semibold hover:opacity-90 card-elev"
                data-testid="billing-upgrade-cta"
              >
                {planKey === "starter" ? "Upgrade to Professional" : "See plans"} <ArrowRight size={14} weight="bold" />
              </Link>
            ) : (
              <a
                href="https://cal.com/romuald-ouattara-wttutm/call-orixa-invest"
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-border rounded-lg px-5 py-3 text-sm font-semibold hover:bg-secondary"
                data-testid="billing-sales-cta"
              >
                Book a product demo <ArrowUpRight size={14} weight="bold" />
              </a>
            )}
          </div>
        </div>

        {/* Usage grid */}
        <div className="mt-8 pt-8 border-t border-border" data-testid="billing-usage">
          <div className="text-overline text-muted-foreground mb-4">Usage this billing period</div>
          <UsageWidget status={status} variant="detailed" />
        </div>
      </div>

      {/* Payment history */}
      <div className="mt-10">
        <h2 className="font-display text-2xl tracking-tight mb-4 flex items-center gap-2">
          <CreditCard size={20} weight="duotone" className="text-primary" /> Payment history
        </h2>
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="billing-history-table">
              <thead className="bg-secondary/60">
                <tr>
                  <th className="text-left p-4 text-overline text-muted-foreground">Date</th>
                  <th className="text-left p-4 text-overline text-muted-foreground">Kind</th>
                  <th className="text-left p-4 text-overline text-muted-foreground">Plan</th>
                  <th className="text-left p-4 text-overline text-muted-foreground">Reference</th>
                  <th className="text-right p-4 text-overline text-muted-foreground">Amount</th>
                  <th className="text-left p-4 text-overline text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">
                    No payments yet. When you subscribe or renew, transactions will appear here.
                  </td></tr>
                )}
                {history.map((h) => (
                  <tr key={h.id} className="border-t border-border hover:bg-secondary/40" data-testid={`billing-history-${h.id}`}>
                    <td className="p-4 whitespace-nowrap">{fmtDate(h.created_at)}</td>
                    <td className="p-4 capitalize">{h.kind}</td>
                    <td className="p-4 capitalize">{h.plan_key}</td>
                    <td className="p-4 font-mono text-xs text-muted-foreground truncate max-w-[180px]">{h.reference || "—"}</td>
                    <td className="p-4 text-right data-large text-base text-primary">{fmtAmount(h.amount, h.currency)}</td>
                    <td className="p-4">
                      <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${
                        h.status === "confirmed" ? "bg-teal-soft text-teal" :
                        h.status === "pending" ? "bg-amber-100 text-amber-700" :
                        "bg-secondary text-muted-foreground"
                      }`}>{h.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Available plans / upgrade grid */}
      {upgradeTargets.length > 0 && (
        <div className="mt-10">
          <h2 className="font-display text-2xl tracking-tight mb-4 flex items-center gap-2">
            <Shield size={20} weight="duotone" className="text-primary" /> Explore other plans
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {upgradeTargets.map((p) => (
              <div key={p.key} className="bg-card border border-border rounded-2xl p-6 flex flex-col" data-testid={`billing-plan-${p.key}`}>
                <div className="text-overline text-primary">{p.recommended ? "Recommended" : p.label}</div>
                <div className="mt-2 font-display text-2xl tracking-tight">Orixa {p.label}</div>
                <div className="mt-1 text-sm text-muted-foreground">{p.tagline}</div>
                <div className="mt-4">
                  {p.contact_only ? (
                    <div className="text-sm">Custom pricing</div>
                  ) : (
                    <div><span className="data-large text-2xl text-primary">{fmtAmount(p.local_price, p.currency)}</span> <span className="text-xs text-muted-foreground">/ month</span></div>
                  )}
                </div>
                <ul className="mt-4 space-y-2 text-sm flex-1">
                  {(p.features || []).slice(0, 5).map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle size={14} weight="fill" className="text-teal mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5">
                  {p.contact_only ? (
                    <a href="https://cal.com/romuald-ouattara-wttutm/call-orixa-invest" target="_blank" rel="noopener noreferrer"
                       className="w-full inline-flex items-center justify-center gap-2 border border-border rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-secondary"
                       data-testid={`billing-plan-${p.key}-cta`}>
                      Book a product demo <ArrowUpRight size={14} weight="bold" />
                    </a>
                  ) : (
                    <Link to={`/pricing`}
                          className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2.5 text-sm font-semibold hover:opacity-90 card-elev"
                          data-testid={`billing-plan-${p.key}-cta`}>
                      Choose {p.label} <ArrowRight size={14} weight="bold" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer note */}
      <div className="mt-10 text-xs text-muted-foreground flex items-start gap-2">
        <WarningCircle size={14} weight="bold" className="mt-0.5 flex-shrink-0" />
        <div>
          Payments are processed securely by Paddle (Merchant of Record) on behalf of Ibn Miyeta Group.
          Orixa Invest never stores your card details. Need an invoice update or VAT change?{" "}
          Use the Manage Subscription button above, or email{" "}
          <a href="mailto:billing@orixa-invest.co" className="text-primary underline">billing@orixa-invest.co</a>.
        </div>
      </div>
    </div>
  );
}
